<template>
  <div
    class="collection-sticky-header critical-component-clear"
    :class="[
      $class(headerState),
      {
        [$class('active')]: active,
      },
    ]"
  >
    <div class="main-collection__header">
      <div
        class="
          collection-sticky-header__title-container
          main-collection__title-container
        "
      >
        <h2
          class="main-collection__title text-heading-l"
          v-text="pageTitle"
        />
      </div>
      <div
        v-if="!collection.specialtyTemplate"
        class="main-collection__options-container"
      >
        <facet-drawer-trigger
          v-if="!hideFilters"
          class="
            collection-sticky-header__facet-trigger
            main-collection__facet-trigger
          "
        />

        <button
          class="main-collection__mobile-toggle"
          type="button"
          :aria-label="$string('collection.toggle_layout')"
          @click="handleLayoutToggle"
        >
          <span
            class="main-collection__layout-icon main-collection__layout-one"
            :class="{ [$class('active')]: mobileLayout === 'one' }"
          >
            <icon-layout-one />
          </span>

          <span
            class="main-collection__layout-icon main-collection__layout-two"
            :class="{ [$class('active')]: mobileLayout === 'two' }"
          >
            <icon-layout-two />
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Collection sticky header (collection-sticky-header)
 * -----------------------------------------------------------------------------
 * Sticky header used on the collection and search template, rendered as part of
 * the site header group.
 *
 * @param {Object} collection - Collection details.
 * @param {Object} search - Search details.
 * @param {Boolean|Object} textFacet - Text filter facet.
 *
 */
import { mapState } from 'vuex'

import FacetDrawerTrigger from '~async/searchspring/facet-drawer-trigger/facet-drawer-trigger'
import IconLayoutOne from '~icons/general/layout-one.svg'
import IconLayoutTwo from '~icons/general/layout-two.svg'

import { setIntersectionObserver } from '~/helpers/general'

export default {
  name: 'CollectionStickyHeader',

  components: {
    FacetDrawerTrigger,
    IconLayoutOne,
    IconLayoutTwo,
  },

  props: {
    collection: {
      type: Object,
      default: () => ({
        specialtyTemplate: false,
        title: 'title',
      }),
      required: true,
    },
    search: {
      type: Object,
      default: () => ({
        page: false,
        query: '',
      }),
      required: true,
    },
    textFacet: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
  },

  emits: {
    'toggle-plp-layout': () => true,
  },

  data() {
    return {
      active: false,
      interval: false,
      isLoading: true,
      localTextFacet: false,
      mobileLayout: localStorage.getItem('grid-layout') ? localStorage.getItem('grid-layout') : 'two',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      headerState: (state) => state.index.scroll.header,
      scrollY: (state) => state.index.scroll.y,
    }),

    /**
     * Compute if collection has text facet.
     * @returns {Boolean}
     */
    hasTextFacet() {
      return (this.localTextFacet || this.textFacet) && !this.collection.specialtyTemplate
    },

    /**
     * Compute hide facets if locale is not en
     * @returns {Boolean}
     */
    hideFilters() {
      const locale = window.cnvs.request.locale.shop_locale.locale
      return locale !== 'en' && !this.enableLocalizedFilters
    },

    /**
     * Compute page title.
     * @returns {String}
     */
    pageTitle() {
      if (this.search.page) {
        return this.$string('search.title', { replace: { query: this.search.query } })
      }

      return this.collection.title
    },
  },

  mounted() {
    cnvs.EventBus.emit('cnvs:update-site-header-height')
    this.setIntersectionObserver()

    cnvs.EventBus.listen('plp:is-loading-update', (isLoading) => {
      this.isLoading = isLoading
    })

    cnvs.EventBus.listen('plp:layout-updated', (gridSize) => {
      this.mobileLayout = gridSize
    })
  },

  methods: {

    /**
     * Set interaction observer.
     */
    setIntersectionObserver() {
      this.interval = setInterval(() => {
        const element = document.querySelector('[js-sticky-header="container"]')

        if (!element) {
          return
        }

        if (this.scrollY > element.offsetHeight) {
          this.active = true
        }

        setIntersectionObserver({
          callback: this.handleComponentIntersection,
          element,
          rootMargin: '0px',
          threshold: 0,
        })

        clearInterval(this.interval)
      }, 1000)
    },

    /**
     * Handle component intersection.
     * @param {Array} entries - Intersection entries.
     */
    handleComponentIntersection(entries) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          this.active = true
          return
        }

        this.active = false
      })
    },

    handleLayoutToggle() {
      cnvs.EventBus.emit('plp:layout-toggle')
    },
  },
}
</script>

<style lang="scss">
@import './collection-sticky-header';
</style>
